import invokeGenericSheetUrl from '../assets/vfx/invoke generic/invoke_generic_sheet.png';
import invokeDrawSheetUrl from '../assets/vfx/invoke draw/invoke_draw.png';
import invokeDefenseSheetUrl from '../assets/vfx/invoke defense/invoke_defense.png';
import yourTurnSheetUrl from '../assets/vfx/turnos/your_turn.png';
import enemyTurnSheetUrl from '../assets/vfx/turnos/enemy_turn.png';
import preimpactSheetUrl from '../assets/vfx/preimpact/preimpact.png';
import enemyHitVfxSheetUrl from '../assets/vfx/hit/hit_vfx_sheet.png';
import starterCoinCaraSheetUrl from '../assets/vfx/coin flip/coin_flip_cara.png';
import starterCoinCruzSheetUrl from '../assets/vfx/coin flip/coin_flip_cruz.png';

export type InvokeAnimationVariant = 'generic' | 'draw' | 'defense';

export const COMBAT_VFX_PRELOAD_SOURCES = [
  invokeGenericSheetUrl,
  invokeDrawSheetUrl,
  invokeDefenseSheetUrl,
  yourTurnSheetUrl,
  enemyTurnSheetUrl,
  preimpactSheetUrl,
  enemyHitVfxSheetUrl,
  starterCoinCaraSheetUrl,
  starterCoinCruzSheetUrl,
] as const;

export type InvokeAnimationConfig = {
  sheetUrl: string;
  frameWidthPx: number;
  frameHeightPx: number;
  frameCount: number;
  columns: number;
  rows: number;
  durationMs: number;
  sizeFactor: number;
  attackSizeFactor: number;
  centerOffsetXFactor: number;
  centerOffsetYFactor: number;
};

export const DEFAULT_INVOKE_ANIMATION_VARIANT: InvokeAnimationVariant = 'generic';

export const INVOKE_ANIMATIONS = {
  generic: {
    sheetUrl: invokeGenericSheetUrl,
    frameWidthPx: 720,
    frameHeightPx: 720,
    columns: 10,
    rows: 9,
    frameCount: 90,
    durationMs: 1500,
    sizeFactor: 1.45,
    attackSizeFactor: 1.45,
    centerOffsetXFactor: 0,
    centerOffsetYFactor: 0.15,
  },
  draw: {
    sheetUrl: invokeDrawSheetUrl,
    frameWidthPx: 512,
    frameHeightPx: 512,
    columns: 10,
    rows: 9,
    frameCount: 90,
    durationMs: 1500,
    sizeFactor: 1.05,
    attackSizeFactor: 1.05,
    centerOffsetXFactor: 0,
    centerOffsetYFactor: 0.15,
  },
  defense: {
    sheetUrl: invokeDefenseSheetUrl,
    frameWidthPx: 512,
    frameHeightPx: 512,
    columns: 10,
    rows: 9,
    frameCount: 90,
    durationMs: 1500,
    sizeFactor: 1.05,
    attackSizeFactor: 1.05,
    centerOffsetXFactor: 0,
    centerOffsetYFactor: 0.15,
  },
} as const satisfies Record<InvokeAnimationVariant, InvokeAnimationConfig>;

export const INVOKE_RELEASE_VFX = INVOKE_ANIMATIONS[DEFAULT_INVOKE_ANIMATION_VARIANT];

export function getInvokeAnimationConfig(variant?: InvokeAnimationVariant | null): InvokeAnimationConfig {
  return INVOKE_ANIMATIONS[variant ?? DEFAULT_INVOKE_ANIMATION_VARIANT] ?? INVOKE_ANIMATIONS[DEFAULT_INVOKE_ANIMATION_VARIANT];
}

export const ENEMY_HIT_VFX = {
  frameWidthPx: 512,
  frameHeightPx: 512,
  frameCount: 34,
  columns: 10,
  rows: 4,
  fps: 60,
  startFrameAtDamageNumber: 15,
  feedbackLifetimeMs: 1600,
} as const;

export const PREIMPACT_VFX = {
  frameCount: 20,
  columns: 10,
  rows: 2,
  fps: 30,
  centerXFactor: 0.5,
  centerYFactor: 0.16,
  sizeFactor: 1,
  minSizePx: 160,
  portraitHeightCapFactor: 0.34,
  maxSizePx: 360,
} as const;


export type TurnHandoffBannerKind = 'player' | 'enemy';
export type TurnHandoffBannerMode = 'text' | 'spritesheet';

export type TurnHandoffBannerTextConfig = {
  playerLabel: string;
  enemyLabel: string;
  fontFamily: string;
  desktopFontSizePx: number;
  mobileFontSizePx: number;
  letterSpacingEm: number;
  strokeWidthPx: number;
};

export type TurnHandoffBannerSpritesheetVariantConfig = {
  sheetUrl: string;
  frameWidthPx: number;
  frameHeightPx: number;
  frameCount: number;
  columns: number;
  rows: number;
  fps: number;
  sizePx: number;
};

export type TurnHandoffBannerSpritesheetConfig = {
  player: TurnHandoffBannerSpritesheetVariantConfig;
  enemy: TurnHandoffBannerSpritesheetVariantConfig;
};

export type TurnHandoffBannerConfig = {
  mode: TurnHandoffBannerMode;
  durationMs: number;
  fadeOutMs: number;
  anchorXFactor: number;
  anchorYFactor: number;
  maxWidthPx: number;
  particleCount: number;
  particleSpreadPx: number;
  glowOpacity: number;
  text: TurnHandoffBannerTextConfig;
  spritesheet: TurnHandoffBannerSpritesheetConfig;
};

export const TURN_HANDOFF_BANNER = {
  mode: 'spritesheet',
  durationMs: 1000,
  fadeOutMs: 500,
  anchorXFactor: 0.5,
  anchorYFactor: 0.44,
  maxWidthPx: 620,
  particleCount: 5,
  particleSpreadPx: 84,
  glowOpacity: 0.16,
  text: {
    playerLabel: 'Your Turn',
    enemyLabel: 'Enemy Turn',
    fontFamily: '"Karma SemiBold", "Cormorant Garamond", Georgia, serif',
    desktopFontSizePx: 58,
    mobileFontSizePx: 42,
    letterSpacingEm: 0.03,
    strokeWidthPx: 1.5,
  },
  spritesheet: {
    player: {
      sheetUrl: yourTurnSheetUrl,
      frameWidthPx: 512,
      frameHeightPx: 512,
      frameCount: 60,
      columns: 10,
      rows: 6,
      fps: 60,
      sizePx: 540,
    },
    enemy: {
      sheetUrl: enemyTurnSheetUrl,
      frameWidthPx: 512,
      frameHeightPx: 512,
      frameCount: 60,
      columns: 10,
      rows: 6,
      fps: 60,
      sizePx: 540,
    },
  },
} as const satisfies TurnHandoffBannerConfig;

export function getTurnHandoffBannerSpritesheetConfig(kind: TurnHandoffBannerKind): TurnHandoffBannerSpritesheetVariantConfig {
  return kind === 'enemy' ? TURN_HANDOFF_BANNER.spritesheet.enemy : TURN_HANDOFF_BANNER.spritesheet.player;
}


export type StarterCoinFlipKind = 'cara' | 'cruz';

export type StarterCoinFlipConfig = {
  sheetUrl: string;
  frameWidthPx: number;
  frameHeightPx: number;
  frameCount: number;
  columns: number;
  rows: number;
  fps: number;
  sizeFactor: number;
};

export const STARTER_COIN_FLIP_VFX = {
  cara: {
    sheetUrl: starterCoinCaraSheetUrl,
    frameWidthPx: 720,
    frameHeightPx: 720,
    frameCount: 120,
    columns: 10,
    rows: 12,
    fps: 60,
    sizeFactor: 2,
  },
  cruz: {
    sheetUrl: starterCoinCruzSheetUrl,
    frameWidthPx: 720,
    frameHeightPx: 720,
    frameCount: 120,
    columns: 10,
    rows: 12,
    fps: 60,
    sizeFactor: 2,
  },
} as const satisfies Record<StarterCoinFlipKind, StarterCoinFlipConfig>;

export function getStarterCoinFlipConfig(kind: StarterCoinFlipKind): StarterCoinFlipConfig {
  return STARTER_COIN_FLIP_VFX[kind];
}
